import { OverlayRef } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, Inject, InjectionToken, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { AuthService } from '../../../services/auth/auth.service';
import { ClientConfigService } from '../../../services/client-config.service';
import { LoaderService } from '../../../services/loader.service';
import { ModalService } from '../../../services/modal.service';
import { TextInputComponent } from "../../../shared/components/text-input/text-input.component";
import { FORGOTTEN_PASSWORD_MODAL_REF, ForgottenPasswordPopupComponent } from '../forgotten-password-popup/forgotten-password-popup.component';
import { REGISTER_MODAL_REF, RegisterPopupComponent } from '../register-popup/register-popup.component';

export const LOGIN_MODAL_REF = new InjectionToken<OverlayRef>(
  "LOGIN_MODAL_REF"
);

@Component({
  selector: 'app-login-popup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TextInputComponent, TranslateModule, FontAwesomeModule],
  templateUrl: './login-popup.component.html',
  styleUrl: './login-popup.component.scss'
})
export class LoginPopupComponent implements OnDestroy {
  loginForm!: FormGroup;
  authFailed: boolean = false;
  authFailedMsg: string | null = null;
  signedIn: boolean = false;
  enabledOAuthProviders: string[] = [];
  private destroy$ = new Subject<void>();

  readonly faTimes = faTimes;

  constructor(@Inject(LOGIN_MODAL_REF) public overlayRef: OverlayRef,
    private readonly authService: AuthService,
    private readonly loaderService: LoaderService,
    private readonly modalService: ModalService,
    private readonly formBuilder: FormBuilder,
    private readonly clientConfigService: ClientConfigService) { }

  ngOnInit(): void {
    this.authFailed = false;
    this.loginForm = this.formBuilder.group(
      {
        email: ['', [Validators.required, Validators.email, Validators.maxLength(256)]],
        password: ['', [Validators.required, Validators.minLength(8)]]
      });

    this.clientConfigService.config$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(config => {
      if (config) {
        this.enabledOAuthProviders = config.enabledOAuthProviders.map(x => x.toLowerCase());
      }
    });
  }

  login() {
    if (!this.loginForm.valid) {
      return;
    }

    this.loaderService.show();

    const email = this.loginForm.get('email')?.value;
    const password = this.loginForm.get('password')?.value;

    this.authService.login(email, password).pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.authService.tryGetUser()),
      tap(() => {
        this.loaderService.hide();
        this.closePopup();
      }),
      catchError((e) => {
        const details = e.error && e.error.detail ? e.error.detail : null;
        this.authFailed = true;
        this.authFailedMsg = details && details !== '' && details != 'Failed' ? details : null;
        this.loaderService.hide();
        return of(null);
      })
    ).subscribe();
  }

  closePopup() {
    this.overlayRef.dispose();
  }

  redirectToRegister() {
    this.closePopup();
    this.modalService.openModal<RegisterPopupComponent>(
      RegisterPopupComponent,
      REGISTER_MODAL_REF
    );
  }

  isProviderEnabled(provider: string): boolean {
    return this.enabledOAuthProviders.includes(provider.toLowerCase());
  }

  loginWithProvider(provider: 'google' | 'facebook' | 'apple') {
    this.authService.loginWithOAuth(provider);
    this.closePopup();
  }

  redirectToForgottenPassword() {
    this.closePopup();
    this.modalService.openModal<ForgottenPasswordPopupComponent>(
      ForgottenPasswordPopupComponent,
      FORGOTTEN_PASSWORD_MODAL_REF
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}